import React, { useMemo, useState } from "react";
import { useApiData } from "@/hooks/useApiData";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";
import RefreshButton from "@/components/RefreshButton";
import { downloadFile, extractError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileText, FileDown, AlertCircle, Loader2, Archive, ShieldCheck, FileCheck2, Files, FolderArchive } from "lucide-react";
import { formatDate } from "@/lib/format";
import { toast } from "sonner";

const GROUPS = [
  { key: "all", label: "All", icon: Files },
  { key: "wtns", label: "WTN", icon: FileText },
  { key: "hwcns", label: "HWCN", icon: FileCheck2 },
  { key: "duty_of_care", label: "Duty of Care", icon: ShieldCheck },
];

const iconFor = (type) => {
  if (type === "duty_of_care") return ShieldCheck;
  if (type === "hwcn") return FileCheck2;
  return FileText;
};

export default function Documents() {
  const { data, loading, refreshing, error, refetch } = useApiData("/portal/documents");
  const [downloading, setDownloading] = useState("");
  const [zipping, setZipping] = useState(false);
  const [packYear, setPackYear] = useState("all");
  const [packLoading, setPackLoading] = useState(false);

  const onRefresh = async () => {
    const ok = await refetch();
    if (ok) toast.success("Documents up to date");
  };

  const groups = data || {};
  const allDocs = useMemo(() => {
    const merge = [
      ...(groups.wtns || []),
      ...(groups.hwcns || []),
      ...(groups.duty_of_care || []),
      ...(groups.waste_transfer_agreements || []),
      ...(groups.certificates || []),
    ];
    return merge.sort((a, b) => new Date(b.issued || 0) - new Date(a.issued || 0));
  }, [groups]);

  const totalCount = allDocs.length;

  // Compliance docs = Duty of Care + transfer notes (WTN/HWCN) + agreements + certificates
  const complianceDocs = useMemo(() => [
    ...(groups.duty_of_care || []),
    ...(groups.wtns || []),
    ...(groups.hwcns || []),
    ...(groups.waste_transfer_agreements || []),
    ...(groups.certificates || []),
  ], [groups]);

  const years = useMemo(() => {
    const set = new Set();
    complianceDocs.forEach((d) => {
      if (!d.issued) return;
      const y = new Date(d.issued).getFullYear();
      if (!isNaN(y)) set.add(y);
    });
    return Array.from(set).sort((a, b) => b - a);
  }, [complianceDocs]);

  const handleDownload = async (doc) => {
    setDownloading(doc.id);
    try {
      await downloadFile({ upstreamUrl: doc.url, filename: `${doc.number || doc.title || "document"}.pdf` });
      toast.success("Document downloaded");
    } catch (e) {
      toast.error(extractError(e, "Download failed"));
    } finally {
      setDownloading("");
    }
  };

  const handleCompliancePack = async () => {
    setPackLoading(true);
    try {
      await downloadFile({
        path: "/portal/compliance-pack",
        params: { year: packYear },
        filename: `Hinton-Compliance-Pack-${packYear === "all" ? "All-Years" : packYear}.zip`,
      });
      toast.success("Your compliance pack is downloading");
    } catch (e) {
      toast.error(extractError(e, "Could not build compliance pack"));
    } finally {
      setPackLoading(false);
    }
  };

  const handleZip = async () => {
    setZipping(true);
    try {
      await downloadFile({ zip: true, filename: "hinton-documents.zip" });
      toast.success("Preparing your documents ZIP\u2026");
    } catch (e) {
      toast.error(extractError(e, "Could not download ZIP"));
    } finally {
      setZipping(false);
    }
  };

  const renderList = (docs, key) => {
    if (!docs || docs.length === 0) {
      return <EmptyState icon={FileText} title="No documents here yet" description="Compliance documents such as Waste Transfer Notes appear here after each collection." testId={`documents-empty-${key}`} />;
    }
    return (
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {docs.map((doc) => {
          const Icon = iconFor(doc.type);
          return (
            <Card key={doc.id} className="flex flex-col rounded-xl border bg-white p-4 shadow-[var(--shadow-sm)] transition-shadow hover:shadow-[var(--shadow-md)]" data-testid={`document-card-${doc.id}`}>
              <div className="flex items-start gap-3">
                <span className="flex h-10 w-10 flex-none items-center justify-center rounded-lg bg-[var(--hh-amber-50)] text-[color:var(--hh-amber-600)]"><Icon className="h-5 w-5" /></span>
                <div className="min-w-0 flex-1">
                  <div className="truncate font-medium text-[color:var(--hh-ink)]" title={doc.title}>{doc.number || doc.title}</div>
                  <div className="truncate text-xs text-muted-foreground">{doc.subtitle}</div>
                </div>
              </div>
              <div className="mt-3 flex items-center justify-between">
                <span className="text-xs text-muted-foreground">Issued {formatDate(doc.issued)}</span>
                <Button size="sm" className="h-8 rounded-lg bg-[hsl(var(--primary))] text-white hover:bg-[var(--hh-amber-600)]" disabled={downloading === doc.id} onClick={() => handleDownload(doc)} data-testid={`document-download-${doc.id}`}>
                  {downloading === doc.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileDown className="h-3.5 w-3.5" />}<span className="ml-1">Download</span>
                </Button>
              </div>
            </Card>
          );
        })}
      </div>
    );
  };

  return (
    <div data-testid="documents-page">
      <PageHeader
        title="Documents"
        subtitle="Download your compliance documents and certificates"
        actions={
          <div className="flex items-center gap-2">
            <RefreshButton refreshing={refreshing} onClick={onRefresh} testId="documents-refresh" />
            <Button onClick={handleZip} disabled={zipping || totalCount === 0} variant="outline" className="rounded-xl" data-testid="documents-download-all-button">
              {zipping ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Archive className="mr-1.5 h-4 w-4" />} Download all (ZIP)
            </Button>
          </div>
        }
      />

      {!loading && !error && complianceDocs.length > 0 && (
        <Card className="mb-6 overflow-hidden rounded-xl border bg-white shadow-[var(--shadow-sm)]" data-testid="compliance-pack-card">
          <div className="flex flex-col gap-4 p-5 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-3">
              <span className="flex h-11 w-11 flex-none items-center justify-center rounded-xl bg-[color:var(--hh-ink)] text-[color:var(--hh-amber)]"><FolderArchive className="h-6 w-6" /></span>
              <div>
                <h3 className="font-display text-lg font-semibold text-[color:var(--hh-ink)]">Compliance pack</h3>
                <p className="max-w-lg text-sm text-muted-foreground">Download a single ZIP of your Duty of Care and Waste Transfer Notes — ready for audits and inspections.</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Select value={packYear} onValueChange={setPackYear}>
                <SelectTrigger className="h-11 w-36" data-testid="compliance-pack-year-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All years</SelectItem>
                  {years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button onClick={handleCompliancePack} disabled={packLoading} className="h-11 rounded-xl bg-[hsl(var(--primary))] text-white hover:bg-[var(--hh-amber-600)]" data-testid="compliance-pack-download-button">
                {packLoading ? <><Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> Building…</> : <><FolderArchive className="mr-1.5 h-4 w-4" /> Download pack</>}
              </Button>
            </div>
          </div>
        </Card>
      )}

      {loading ? (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">{[...Array(6)].map((_, i) => <Skeleton key={i} className="h-28 rounded-xl" />)}</div>
      ) : error ? (
        <Alert variant="destructive" data-testid="documents-error"><AlertCircle className="h-4 w-4" /><AlertTitle>Couldn't load documents</AlertTitle><AlertDescription className="flex items-center gap-3">{error}<Button size="sm" variant="outline" onClick={refetch}>Retry</Button></AlertDescription></Alert>
      ) : (
        <Tabs defaultValue="all">
          <TabsList className="mb-5 flex-wrap" data-testid="documents-tabs">
            {GROUPS.map((g) => {
              const count = g.key === "all" ? totalCount : (groups[g.key] || []).length;
              return (
                <TabsTrigger key={g.key} value={g.key} data-testid={`documents-tab-${g.key}`}>
                  <g.icon className="mr-1.5 h-4 w-4" /> {g.label}
                  <span className="ml-1.5 rounded-full bg-[var(--hh-band)] px-1.5 text-xs">{count}</span>
                </TabsTrigger>
              );
            })}
          </TabsList>
          <TabsContent value="all">{renderList(allDocs, "all")}</TabsContent>
          <TabsContent value="wtns">{renderList(groups.wtns, "wtns")}</TabsContent>
          <TabsContent value="hwcns">{renderList(groups.hwcns, "hwcns")}</TabsContent>
          <TabsContent value="duty_of_care">{renderList(groups.duty_of_care, "duty_of_care")}</TabsContent>
        </Tabs>
      )}
    </div>
  );
}
